/**
 * pendingInvite — durable marker for an in-flight team invitation
 * (task #45, 2026-08-10).
 *
 * The accept page records the invite here the moment it loads, and clears it
 * on every terminal outcome (accepted, already a member, expired, invalid,
 * hard error). While a pending invite exists, PendingInviteGate funnels a
 * signed-in user back to the acceptance page from any app surface — so an
 * invitee who closes the browser mid-journey picks up exactly where they
 * left off on their next visit, with no email re-click needed.
 *
 * localStorage (not sessionStorage) deliberately: the whole point is
 * surviving a closed browser.
 */

const KEY = 'prest0_pending_team_invite';

export interface PendingInvite {
  /** Full accept-page path incl. token/inviteId/accountId query. */
  url: string;
  /** Stored for staleness pruning. */
  savedAtMs: number;
}

/** Invitations expire server-side after 7 days — prune anything older. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function savePendingInvite(url: string): void {
  try {
    if (!url.startsWith('/accept-invite')) return;
    const entry: PendingInvite = { url, savedAtMs: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable — resume simply won't survive a restart */
  }
}

export function getPendingInvite(): PendingInvite | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as PendingInvite;
    if (!entry?.url || typeof entry.url !== 'string' || !entry.url.startsWith('/accept-invite')) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (typeof entry.savedAtMs === 'number' && Date.now() - entry.savedAtMs > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
